<template>
  <v-form ref="ETABLISSEMENT_COMMERCIAL" @submit.prevent="sectionSave" lazy-validation class="section">
    <div>
      <section-header v-if="!hideTitle"
                      :ressource="ressource"
                      :isExtended="extended"
                      :dialog="dialog"
                      :picto="picto()"
                      :fiche=fiche
                      :isAbstract=isAbstract
                      @extended=setExtended
                      @closeDialog=closeDialog
                      ref="ETABLISSEMENT_COMMERCIAL">
      </section-header>
      <div v-if="extended" id="field-container" :class="dialog ? 'p-0 p-md-3' : 'py-2'">
        <etablissement-siret v-if="isDisplayed('SIRET')"
                             ref="SIRET"
                             :theme="theme"
                             :showMissing="showMissing"
                             v-model:value=siret
                             @status=statusSiret
                             :over-ride-placeholder=placeholderSiret
                             :required="isRequired('SIRET')"
                             :contextId=etablissement.id
                             :readOnly="readOnly('SIRET')"
                             :objType=objType
                             :fiche=fiche
                             :sectionName="sectionName">
        </etablissement-siret>
        <etablissement-alias v-if="isDisplayed('ALIAS')"
                             ref="ALIAS"
                             :theme="theme"
                             :showMissing="showMissing"
                             v-model:value=alias
                             @status=statusAlias
                             :over-ride-placeholder=placeholderAlias
                             :required="isRequired('ALIAS')"
                             :contextId=etablissement.id
                             :readOnly="readOnly('ALIAS')"
                             :objType=objType
                             :fiche=fiche
                             :sectionName="sectionName">
        </etablissement-alias>
        <etablissement-nom v-if="isDisplayed('NOM')"
                           ref="NOM"
                           :theme="theme"
                           :showMissing="showMissing"
                           v-model:value=nom
                           @status=statusNom
                           :over-ride-placeholder=placeholderNom
                           :required="isRequired('NOM')"
                           :contextId=etablissement.id
                           :readOnly="readOnly('NOM')"
                           :objType=objType
                           :fiche=fiche
                           :sectionName="sectionName">
        </etablissement-nom>
        <contact-select v-if="isDisplayed('COMMERCIAL_ID')"
                        ref="COMMERCIAL_ID"
                        :theme="theme"
                        :showMissing="showMissing"
                        v-model:value=commercialId
                        :moduleId=moduleId
                        libelle="'Commercial'"
                        newLabel=''
                        selectText=""
                        @status=statusCommercialId
                        :requiredAttributs="requiredAttributs"
                        :autoSelect=true
                        :ressource=ressourceContactSelect
                        :filters=commercialIdFilter
                        :toggleReset=resetCommercialId
                        :required="isRequired('COMMERCIAL_ID')"
                        :contextId=etablissement.id
                        :readOnly="readOnly('COMMERCIAL_ID')"
                        :objType=objType
                        :fiche=fiche
                        :sectionName="sectionName">
        </contact-select>
        <etablissement-user-disabled v-if="isDisplayed('USER_DISABLED')"
                                     ref="USER_DISABLED"
                                     :theme="theme"
                                     :showMissing="showMissing"
                                     v-model:value=userDisabled
                                     @status=statusUserDisabled
                                     :required="isRequired('USER_DISABLED')"
                                     :contextId=etablissement.id
                                     :readOnly="readOnly('USER_DISABLED')"
                                     :objType=objType
                                     :fiche=fiche
                                     :sectionName="sectionName">
        </etablissement-user-disabled>
      </div>
    </div>
    <div v-if="extended" class="d-flex flex-row-reverse">
      <loader-button v-if="saveButton"
                     class="ms-2"
                     type="submit"
                     :loading="saveLoading"
                     :label="saveLabel"
                     :disabled="!updatedAndValid">
      </loader-button>
      <div v-for="(button, index) in extraButtons" :key="index">
        <loader-button class="ms-2"
                       :outlined="button.outlined"
                       @callback="button.callback()"
                       :type="button.type"
                       :label="button.label"
                       :loading="button.loading"
                       :disabled="button.disabled">
        </loader-button>
      </div>
    </div>
    <div class="d-flex flex-row-reverse" v-if="saveFailed && saveButton && extended">
      <p class="text-12 text-bold error-text">{{errorTxt}}</p>
    </div>
  </v-form>
</template>

<script lang="ts">
  import { defineComponent } from "vue";
  import CrudEtablissement from '@/generated/cruds/CrudEtablissement';
  // eslint-disable-next-line
  import { GenControl, GenSection, RessourceGenControl, RessourceGenSection } from '@/generated/ressources/generatedRessources';
  import { FwkButtons, RessourceFwkButton, RessourceFwkSection, FwkSections } from '@/ressources/globalRessources';
  import { Sections } from '@/generated/classes/AppSchema';
  import Button, { ButtonCallback } from '@/framework/base/classes/Button';
  import { ColumnFilter, ListChoix } from '@/extend/classes/ListData';
  import { ObjTypes } from '@/generated/classes/ObjTypes';

  export default defineComponent({
    extends:  CrudEtablissement,
    components: {},
    props: {
      hideTitle: {
        type: Boolean,
      },
      isAbstract: {
        type: Boolean,
      },
    },

    data() {
      return {
        sectionName: Sections.ETABLISSEMENT_COMMERCIAL,
        displayMap: new Map<string, Boolean>,
        readOnlyMap: new Map<string, Boolean>,
        requiredMap: new Map<string, Boolean>,
        placeholderSiret: null,
        placeholderAlias: null,
        placeholderNom: null,
      }
    },

    computed: {
      saveLabel () { 
        return RessourceFwkButton(FwkButtons.ENREGISTRER).label;
      },

      errorTxt () { 
        return RessourceFwkSection(FwkSections.ERROR_TEXT).label;
      },

      ressourceContactSelect () { return RessourceGenControl(GenControl.ETABLISSEMENT_COMMERCIAL_CONTACT_SELECT); },

      ressource () { return RessourceGenSection(GenSection.ETABLISSEMENT_COMMERCIAL); },

      fields (): string[] {
        return ['SIRET', 'ALIAS', 'NOM', 'COMMERCIAL_ID', 'USER_DISABLED'];
      },

      extraButtons (): Button[] {
        return [];
      },

      commercialIdFilter (): ColumnFilter[] {
        const res: ColumnFilter[] = [];
        return res;
      },
    },
    methods: {
      getRequiredAttributs(): string[] {
        const res: string[] = [];
        if (this.requiredAttributs) {
          this.requiredAttributs.forEach((attrib) => {
            const value = attrib.split(".");
            if (value[0] == this.sectionName) {
              res.push(value[1])
            }
          })
        }
        return res;
      },

      requiredMissing(requiredAttributs: string[], value: string): boolean {
        return requiredAttributs.length > 0 && requiredAttributs.includes(value);;
      },

      readOnly (value: string): boolean {
        let res = false;
        if (!this.editEtablissement.canUpdate) {
          return true;
        }
        if (this.readOnlyMap.size == 0) {
          this.readOnlyMap.set("SIRET", this.editEtablissement.siret != '');
          this.readOnlyMap.set("ALIAS", this.editEtablissement.alias != '');
          this.readOnlyMap.set("NOM", this.editEtablissement.nom != '');
          this.readOnlyMap.set("COMMERCIAL_ID", false);
          this.readOnlyMap.set("USER_DISABLED", false);
        }
        const readOnly = this.readOnlyMap.get(value);
        if (readOnly != undefined) {
          res = readOnly;
        }
        return res;
      },

      isDisplayed (value: string): boolean {
        let res = true;
        if (this.displayMap.size == 0) {
        const requiredAttributs = this.getRequiredAttributs();
          this.displayMap.set("SIRET", true);
          this.displayMap.set("ALIAS", true);
          this.displayMap.set("NOM", true);
          this.displayMap.set("COMMERCIAL_ID", true);
          this.displayMap.set("USER_DISABLED", this.requiredMissing(requiredAttributs, "USER_DISABLED") || !!this.fiche);
        }
        const display = this.displayMap.get(value);
        if (display != undefined) {
          res = display;
        }
        return res;
      },

      isRequired (value: string): boolean {
        let res = true;
        const requiredAttributs = this.getRequiredAttributs();
        if (this.requiredMap.size == 0) {
          this.requiredMap.set("SIRET", true);
          this.requiredMap.set("ALIAS", true);
          this.requiredMap.set("NOM", true);
          this.requiredMap.set("COMMERCIAL_ID", true);
          this.requiredMap.set("USER_DISABLED", this.requiredMissing(requiredAttributs, "USER_DISABLED") || !this.fiche);
        }
        const required = this.requiredMap.get(value);
        if (required != undefined) {
          res = required;
        }
        return res;
      },

      picto (): string {
        return 'SectionPictoCommercial' 
      },

      sectionSave (): void {
        return this.save();
      }
    }
  })
</script>
